import React, { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTemplateLoader } from '../hooks/useTemplateLoader';
import SEO from '../components/layout/SEO';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getMediaUrl } from '../utils/environment';
import '../styles/templates-gallery.css';
import { TemplateGallerySEO } from '../components/layout/SEO';  
import { usePageData } from '../contexts/DataContext';

const TemplatesGallery = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  
  const { getAllTemplates, getTemplateCategories } = useTemplateLoader();
  const { data: templatePage } = usePageData('templatePage');

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, selectedCategory, selectedType]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [templatesData, categoriesData] = await Promise.all([
        getAllTemplates(),
        getTemplateCategories()
      ]);
      
      console.log('Templates data:', templatesData);
      console.log('Categories data:', categoriesData);
      
      setTemplates(templatesData);
      setFilteredTemplates(templatesData);
      
      const formattedCategories = [
        { value: 'all', label: 'All Categories' },
        ...categoriesData.map(cat => ({
          value: cat.slug,
          label: cat.name
        }))
      ];
      
      setCategories(formattedCategories);
      
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load templates');
      setTemplates([]);
      setFilteredTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(term) ||
        template.description.toLowerCase().includes(term) ||
        (template.category_name && template.category_name.toLowerCase().includes(term))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template =>
        template.category_slug === selectedCategory
      );
    }
    
    if (selectedType !== 'all') {
      if (selectedType === 'premium') {
        filtered = filtered.filter(template => template.is_premium);
      } else if (selectedType === 'free') {
        filtered = filtered.filter(template => !template.is_premium);
      }
    }
    
    setFilteredTemplates(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  const handleRetry = () => {
    fetchTemplates();
  };

  React.useEffect(() => {
    document.body.style.paddingTop = '50px';
    return () => {
      document.body.style.paddingTop = '0';
    };
  }, []);

  if (loading) {
    return (
      <HelmetProvider>
        <div className="templates-page">
           <TemplateGallerySEO pageData={templatePage} /> 
        </div>
      </HelmetProvider>
    );
  }

  if (error) {
    return (
      <HelmetProvider>
        <div className="templates-page">
          <SEO title="Templates - Resumeslip" />
          <div className="container">
            <div className="error-state">
              <h2>Unable to Load Templates</h2>
              <p>{error}</p>
              <button onClick={handleRetry} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <div className="templates-page">
       <TemplateGallerySEO pageData={templatePage} />
        
        <div className="container">
          <div className="templates-header">
            <h1>{templatePage?.title || "Professional Templates"}</h1>
            <p className="subtitle">
              {templatePage?.description || "Choose from beautifully designed templates for resumes, CVs, and slips. Edit and download instantly."}
            </p>
          </div>

          <div className="templates-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-options">
              <div className="filter-group">
                <label>Category:</label>
                <div className="filter-buttons">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(cat.value)}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* <div className="filter-group">
                <label>Type:</label>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${selectedType === 'all' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn ${selectedType === 'free' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('free')}
                  >
                    Free
                  </button>
                  <button
                    className={`filter-btn ${selectedType === 'premium' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('premium')}
                  >
                    Premium
                  </button>
                </div>
              </div> */}
            </div>
          </div>

          <div className="results-info">
            <p>
              Showing {filteredTemplates.length} of {templates.length} templates
              {searchTerm && ` for "${searchTerm}"`}
              {selectedCategory !== 'all' && ` in ${categories.find(c => c.value === selectedCategory)?.label}`}
            </p>
          </div>

          {filteredTemplates.length > 0 ? (
            <div className="templates-grid">
              {filteredTemplates.map((template) => (
                <Link 
                  to={`/editor/${template.slug}`}
                  className="template-card" 
                  key={template.id}
                >
                  <div className="template-image">
                    <img
                      src={template.thumbnail_url || getMediaUrl(template.thumbnail) || '/default-template.jpg'}
                      alt={template.name}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/default-template.jpg';
                      }}
                    />
                    {template.is_premium && (
                      <span className="premium-badge">Premium</span>
                    )}
                    {template.category_name && (
                      <span className="category-badge">
                        {template.category_name}
                      </span>
                    )}
                  </div>
                  
                  <div className="template-content">
                    <div className="template-header">
                      <h3>{template.name}</h3>
                    </div>
                    <p className="template-description">
                      {template.description || 'Professional template'}
                    </p>
                    
                    <div className="template-meta">
                      <span className="downloads">
                        <span className="icon">⬇️</span> {template.downloads || 0} downloads
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h3>No templates found</h3>
              <p>
                {searchTerm
                  ? `No templates match "${searchTerm}". Try a different search term.`
                  : selectedCategory !== 'all'
                  ? `No templates in the "${categories.find(c => c.value === selectedCategory)?.label}" category.`
                  : 'No templates available at the moment.'}
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setSelectedType('all');
                }}
                className="btn-primary"
              >
                Clear Filters
              </button>
            </div>
          )}

          {categories.length > 1 && (
            <div className="categories-section">
              <h2>Template Categories</h2>
              <div className="categories-grid">
                {categories.filter(cat => cat.value !== 'all').map((cat) => (
                  <div 
                    key={cat.value}
                    className="category-card"
                    onClick={() => handleCategoryChange(cat.value)}
                  >
                    <div className="category-icon">📄</div>
                    <h4>{cat.label}</h4>
                    <p>
                      {templates.filter(t => t.category_slug === cat.value).length} templates
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </HelmetProvider>
  );
};

export default TemplatesGallery;